import { allFromSnapshot, isAdminConnection, } from "@buildwithflux/firestore-compatibility-layer";
import { createStarredDocumentData, createStarredDocumentUid, ProfileDocumentType, } from "@buildwithflux/models";
import chunk from "lodash/chunk";
import map from "lodash/map";
import uniq from "lodash/uniq";
export class FirestoreStarredDocumentRepository {
    db;
    documentRepository;
    constructor(db, documentRepository) {
        this.db = db;
        this.documentRepository = documentRepository;
    }
    /**
     * @inheritDoc
     * @todo rewrap with DocumentStorage.isDocumentStarred span
     */
    async isStarred(documentUid, userUid) {
        const starredDocumentUid = createStarredDocumentUid(documentUid, userUid);
        return this.db
            .starredDocumentData(starredDocumentUid)
            .get()
            .then((snapshot) => {
            return snapshot.exists;
        })
            .catch(() => {
            return false; // TODO: don't silence errors here, throw them
        });
    }
    /** @inheritDoc */
    async star(documentUid, userUid) {
        const starredDocument = createStarredDocumentData(documentUid, userUid);
        return this.db.starredDocumentData(starredDocument.uid).set(starredDocument).then();
    }
    /** @inheritDoc */
    async unstar(documentUid, userUid) {
        const starredDocumentUid = createStarredDocumentUid(documentUid, userUid);
        return this.db.starredDocumentData(starredDocumentUid).delete().then();
    }
    /** @inheritDoc */
    async getByOwnerUid(ownerUid) {
        return allFromSnapshot(await this.db.starredDocumentCollection().where("owner_uid", "==", ownerUid).get());
    }
    /** @inheritdoc */
    subscribeToAllByOwnerUid(ownerUid, onChange, onError) {
        return this.db
            .starredDocumentCollection()
            .where("owner_uid", "==", ownerUid)
            .onSnapshot((snap) => {
            onChange(allFromSnapshot(snap));
        }, onError);
    }
    /** @inheritDoc */
    async getByDocumentUid(documentUid) {
        return allFromSnapshot(await this.db.starredDocumentCollection().where("document_uid", "==", documentUid).get());
    }
    async getDocumentUidWithCounts(documentCount) {
        const snapshot = await this.db.starredDocumentCollection().get();
        const documentsData = snapshot.docs.map((doc) => doc.data());
        const documentUidCountMap = documentsData.reduce((key, data) => {
            if (!key[data.document_uid]) {
                key[data.document_uid] = 0;
            }
            key[data.document_uid]++;
            return key;
        }, {});
        const documentUidCountArray = Object.keys(documentUidCountMap).map((documentUid) => ({
            documentUid,
            count: documentUidCountMap[documentUid],
        }));
        documentUidCountArray.sort((a, b) => b.count - a.count);
        return documentUidCountArray.slice(0, documentCount);
    }
    createStream({ documentsOwnerUid, accessListForQuery, limit, documentTypeFilter = ProfileDocumentType.all, }) {
        let streamEnded = false;
        let lastDocument = null;
        const repo = this;
        return {
            fetchNextPage(onSuccess, onError) {
                if (streamEnded) {
                    onSuccess([]);
                    return () => { };
                }
                let query = repo.db
                    .starredDocumentCollection()
                    .where("owner_uid", "==", documentsOwnerUid)
                    .orderBy("created_at", "desc")
                    .limit(limit);
                if (lastDocument) {
                    query = query.startAfter(lastDocument);
                }
                return query.onSnapshot((snapshot) => {
                    lastDocument = snapshot.docs[snapshot.docs.length - 1] ?? null;
                    repo.getStarredDocumentsFromUids(snapshot.docs.map((starred) => starred.data().document_uid), accessListForQuery, documentTypeFilter)
                        .then(onSuccess)
                        .catch(onError);
                }, onError);
            },
            reset() {
                lastDocument = null;
                streamEnded = false;
            },
        };
    }
    async getSummaryForDocument(documentUid, ownerUid) {
        const underlying = this.db.underlyingConnection();
        if (!isAdminConnection(underlying)) {
            throw new Error("Expected admin connection: only admin connections can .select()");
        }
        const docs = allFromSnapshot(await underlying.collection("starredDocuments").select("owner_uid")
            .where("document_uid", "==", documentUid)
            .get());
        const uids = uniq([...map(docs, "owner_uid"), ownerUid]);
        return { count: uids.length, uids };
    }
    async getStarredDocumentsFromUids(starredDocumentUids, accessListForQuery, documentTypeFilter = ProfileDocumentType.all) {
        const fetchChunkSize = 10;
        const starredDocumentUidChunks = chunk(starredDocumentUids, fetchChunkSize);
        const starredDocuments = [];
        for (const starredDocumentUidsChunk of starredDocumentUidChunks) {
            const documents = await this.documentRepository.getBaseChunk({
                uids: starredDocumentUidsChunk,
                accessListForQuery,
                documentTypeFilter,
            });
            starredDocuments.push(...documents);
        }
        return starredDocuments;
    }
}
export function createFirestoreStarredDocumentRepository(db, documentRepository) {
    return new FirestoreStarredDocumentRepository(db, documentRepository);
}
//# sourceMappingURL=FirestoreStarredDocumentRepository.js.map